import React from 'react';
import { withTranslation, i18n } from 'config/i18n';
import { useLayoutState } from 'components/shared/Layout';
// Material UI core
import dynamic from 'next/dynamic';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import MuiButton from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Link from 'components/shared/Link';
// Material UI icons
import HelpIcon from '@material-ui/icons/Help';
import EmailIcon from '@material-ui/icons/Email';
import PhoneIcon from '@material-ui/icons/Phone';
// helper Function
import getLocalizedUrl from 'utils/getLocalizedUrl';

// React components
const ContactUsDialog = dynamic(() => import('common/components/ContactUsDialog'), { ssr: false });
const PhonesDialog = dynamic(() => import('components/layout/Footer/NeedHelp/PhonesDialog'), {
  ssr: false,
});
const Button = withStyles({
  root: {
    width: '100%',
  },
  label: {
    flexDirection: 'column',
  },
})(MuiButton);

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.grey[900],
    padding: theme.spacing(2.5),
    color: theme.palette.text.primary,
  },
  grid: {
    textAlign: 'center',
  },
  title: {
    fontSize: '2rem',
    fontWeight: 500,
    marginTop: theme.spacing(1),
  },
  icon: {
    fontSize: '2.2rem',
  },
  needHelpOptions: {
    color: theme.palette.common.white,
  },
}));

const NeedHelp = ({ t }) => {
  const classes = useStyles();
  const { layoutState } = useLayoutState();

  const [shouldRenderDialog, setShouldRenderDialog] = React.useState({
    contactUs: false,
    phones: false,
  }); // Only render dialogs into the DOM on demand

  const [isOpen, setOpen] = React.useState(false); // Contact-us dialog
  const [isPhonesOpen, setPhonesOpen] = React.useState(false); // Phones dialog

  const handleContactUsButtonClick = () => {
    setShouldRenderDialog({ contactUs: true });
    setOpen(true);
  };

  const handlePhonesButtonClick = () => {
    setShouldRenderDialog({ phones: true });
    setPhonesOpen(true);
  };

  return (
    <div className={classes.root}>
      <Container>
        <Grid container spacing={2}>
          <Grid item lg={3} xs={12} className={classes.grid}>
            <Typography component="div" className={classes.title}>
              {t('fe_es_layout:footer.need_help')}
            </Typography>
          </Grid>
          <Grid item lg={3} xs={4} className={classes.grid}>
            <Button
              className={classes.needHelpOptions}
              component={Link}
              target="_blank"
              href={getLocalizedUrl('/faqs', i18n.language)}
            >
              <HelpIcon className={classes.icon} />
              <Typography variant="button">
                {t('fe_es_layout:footer.frequent_questions_mobile')}
              </Typography>
            </Button>
          </Grid>
          <Grid item lg={3} xs={4} className={classes.grid}>
            <Button className={classes.needHelpOptions} onClick={handleContactUsButtonClick}>
              <EmailIcon className={classes.icon} />
              <Typography variant="button">{t('fe_es_layout:footer.email')}</Typography>
            </Button>
            {shouldRenderDialog.contactUs && isOpen && (
              <ContactUsDialog
                open={isOpen}
                onClose={() => {
                  setOpen(false);
                  setShouldRenderDialog({
                    ...shouldRenderDialog,
                    contactUs: false,
                  });
                }}
                title={t('fe_es_layout:contact_us_help.contact_eagleShare')}
                userId={layoutState.current_user ? layoutState.current_user.id : null}
                caseParam="eagleshare"
              />
            )}
          </Grid>
          <Grid item lg={3} xs={4} className={classes.grid}>
            <Button className={classes.needHelpOptions} onClick={handlePhonesButtonClick}>
              <PhoneIcon className={classes.icon} />
              <Typography variant="button">{t('fe_es_layout:footer.phone')}</Typography>
            </Button>
            {shouldRenderDialog.phones && isPhonesOpen && (
              <PhonesDialog
                open={isPhonesOpen}
                onClose={() => setPhonesOpen(false)}
                phones={
                  layoutState.customer_support ? layoutState.customer_support.eagleshare.phones : []
                }
                title={t('fe_es_layout:contact_us_help.contact_eagleShare')}
              />
            )}
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default withTranslation('fe_es_layout')(NeedHelp);
